import React, {useState} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import {useNavigate} from "react-router";
import {SeoHead} from "./SeoHead";
import {RichEditor} from "../admin/RichEditor";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {useMeditationContent} from "../firebase/useMeditationContent";
import {ProseArticle} from "./ProseArticle";
import {AccessibleAudioPlayer} from "./AccessibleAudioPlayer";
import {MEDITATION_TRACKS, DEFAULT_TRACK} from "../meditation/meditationTracks";
import {MeditationTrackSelector} from "../meditation/MeditationTrackSelector";

export const DEFAULT_MINDFULNESS_TRACK = DEFAULT_TRACK;

export const Meditation = () => {
    const navigate = useNavigate();
    const [selectedTrack, setSelectedTrack] = useState(DEFAULT_TRACK);
    const [forceRefresh, setForceRefresh] = useState(0);
    const [meditationContent, updateMeditationContent] = useMeditationContent({forceRefresh});
    const isAdmin = useCheckIfAdmin();
    const [editMode, setEditMode] = useState(false);

    const saveHandler = data => {
        updateMeditationContent(data);
        setForceRefresh(fr => fr + 1);
        setEditMode(false);
    };

    return (
        <Box sx={{height: "100%", py: {xs: 2, sm: 3}}}>
            <SeoHead
                title="Meditasjon og oppmerksomt nærvær"
                description="Guidede meditasjonslydfiler (8 min, 14 min, 20 min, 30 min) og oppmerksomt nærvær for å gi slipp på bekymringer og finne ro."
                path="/meditation"
            />
            <Container maxWidth="md">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/#meditasjon")}
                        sx={{
                            minHeight: 44,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "text.secondary",
                            borderRadius: 2,
                            "&:hover": {color: "primary.main"},
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "primary.main",
                                outlineOffset: 2,
                            },
                        }}
                    >
                        Tilbake til forsiden
                    </Button>

                    {isAdmin && !editMode && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => setEditMode(true)}
                            sx={{
                                minHeight: 44,
                                px: 2.5,
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: 2,
                                boxShadow: "none",
                                "&:focus-visible": {
                                    outline: "2px solid",
                                    outlineColor: "primary.main",
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            Rediger veiledningstekst
                        </Button>
                    )}
                </Box>

                {isAdmin && editMode && (
                    <Box sx={{mb: 4}}>
                        <Alert
                            severity="info"
                            sx={{
                                mb: 3,
                                borderRadius: 3,
                                border: "1px solid rgba(40, 82, 56, 0.15)",
                                bgcolor: "rgba(243, 247, 244, 0.9)",
                                color: "text.primary",
                                "& .MuiAlert-icon": {color: "primary.main"},
                            }}
                        >
                            <Typography variant="body2" sx={{fontWeight: 500}}>
                                <strong>Redigeringsmodus:</strong> Gjør endringer i veiledningsteksten nedenfor.
                                Forhåndsvisningen oppdateres automatisk. Klikk «Lagre» for å publisere.
                            </Typography>
                        </Alert>
                        <RichEditor
                            initialData={meditationContent}
                            onSave={saveHandler}
                            onCancel={() => setEditMode(false)}
                        />
                    </Box>
                )}

                {!editMode && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: {xs: 2.5, sm: 5},
                            mb: 4,
                            borderRadius: 4,
                            bgcolor: "#FFFFFF",
                            border: "1px solid rgba(40, 82, 56, 0.08)",
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                        }}
                    >
                        <Box sx={{mb: 3}}>
                            <Typography
                                variant="h4"
                                component="h1"
                                gutterBottom
                                sx={{fontWeight: 700, color: "primary.main"}}
                            >
                                Meditasjonsøvelser
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Praktiske oppmerksomhetsøvelser for å trene hjernen til å gi slipp på kvernetanker,
                                roe ned fysiologisk uro og finne tilstedeværelse i hverdagen.
                            </Typography>
                        </Box>

                        <MeditationTrackSelector
                            tracks={MEDITATION_TRACKS}
                            selectedTrackId={selectedTrack.id}
                            onSelectTrack={setSelectedTrack}
                        />
                        <AccessibleAudioPlayer
                            key={selectedTrack.id}
                            title={selectedTrack.title}
                            subtitle={selectedTrack.subtitle}
                            src={selectedTrack.src}
                            transcript={selectedTrack.transcript}
                        />
                        <Divider sx={{my: 4}} />
                        <Box sx={{mt: 2}}>
                            <ProseArticle html={meditationContent} />
                        </Box>
                    </Paper>
                )}
            </Container>
        </Box>
    );
};